import { Box, Button, ButtonProps, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { useMutation } from "@outschool/ui-apollo";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  ModalActionButtons,
  Select
} from "@outschool/ui-legacy-component-library";
import React, { FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";

import * as Enrollment from "../../../shared/Enrollment";
import * as Env from "../../../shared/Env";
import {
  SendMessageToUserMutation,
  SendMessageToUserMutationVariables
} from "../../../shared/generated/graphql";
import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import { useUserMode } from "../../lib/UserMode";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import useTransferSections, {
  TransferSection,
  useMapAsOptions
} from "../../queries/TransferSectionQueries";
import ActivitySummary from "../enrolling/ActivitySummary";
import ErrorMessage from "../ErrorMessage";
// @ts-ignore - JSX import
import { sendMessageMutation } from "../MessageModal";
import PageModal from "../PageModal";
import {
  EnrolledClassActivity,
  EnrolledClassEnrollment,
  EnrolledClassGiftEnrollment,
  EnrolledClassSection
} from "./EnrolledClassTypes";

type ParentTransferButtonProps = ButtonProps & {
  section: EnrolledClassSection;
  activity: EnrolledClassActivity;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
};

export default React.forwardRef(ParentTransferButton);

function ParentTransferButton(
  {
    section,
    activity,
    enrollment,
    onClick,
    ...buttonProps
  }: ParentTransferButtonProps,
  ref: React.Ref<any>
) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentTransferButton"
  );
  const [open, setOpen] = useState(false);
  return (
    <React.Fragment>
      <Button
        ref={ref}
        variant="link"
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onClick?.(event);
          setOpen(true);
        }}
        {...buttonProps}
      >
        {t`Transfer`}
      </Button>
      <PageModal open={open} onClose={() => setOpen(false)}>
        <TransferModalBody
          section={section}
          activity={activity}
          enrollment={enrollment}
          close={() => setOpen(false)}
        />
      </PageModal>
    </React.Fragment>
  );
}

function composeContent(
  activity: EnrolledClassActivity,
  section: TransferSection,
  note: string,
  userTimeZone: string
): string {
  const educatorTimezone = User.timeZone(activity.leader);
  const isOngoing = !!activity.is_ongoing_weekly;
  const startTime = isOngoing
    ? section.nextOngoingMeeting?.start_time
    : section.meetings[0].start_time;
  const meetingTime = Time.formatDateTimeWithWeekday(
    startTime,
    educatorTimezone
  );

  return `\
${note}

------
Transfer requested into section that meets on ${meetingTime}
Time displayed in: ${Time.timeZoneName(educatorTimezone)}
Learner's time zone: ${Time.timeZoneName(userTimeZone)}
Link to Classroom: ${Routes.websiteRoutes.classroomUrl(section.uid)}
`;
}

type TransferModalBodyProps = ParentTransferButtonProps & {
  close: () => void;
};

function TransferModalBody({
  enrollment,
  activity,
  section,
  close
}: TransferModalBodyProps) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentTransferButton"
  );
  const userMode = useUserMode();
  const { setTopNotice } = useTopNoticeContext();
  const { timeZone: userTimeZone } = useTimeZone();
  const { loading, sections } = useTransferSections(activity, section);
  const transferNoteInputRef = useRef<HTMLTextAreaElement>(null);
  const sectionInputRef = useRef<HTMLSelectElement>(null);
  const [error, setError] = useState<string | null>("");
  const [requestedSection, setRequestedSection] =
    useState<TransferSection | null>(null);
  const defaultTransferNote = t`Would it be possible to make this transfer? `;
  const [transferNote, setTransferNote] = useState(defaultTransferNote);
  const [sendPrivateMessage] = useMutation<
    SendMessageToUserMutation,
    SendMessageToUserMutationVariables
  >(sendMessageMutation);

  const options = useMapAsOptions(activity, sections, userTimeZone);
  const studentName = Enrollment.displayName(enrollment);

  const [isSendingPrivateMessage, setIsSendingPrivateMessage] = useState(false);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!requestedSection) {
      sectionInputRef.current?.focus();
      setError(t`Please choose a time`);
      return;
    }
    setIsSendingPrivateMessage(true);
    const content = composeContent(
      activity,
      requestedSection,
      transferNote,
      userTimeZone
    );
    try {
      const { data } = await sendPrivateMessage({
        variables: {
          recipientUids: [activity.leader.uid],
          messageInput: {
            content,
            activityUid: activity.uid,
            sectionUid: section.uid,
            details: {
              action: "Enrolled parent to teacher",
              activityTitle: activity.title,
              subject: `Requesting transfer for ${
                studentName || "a gift enrollment"
              } on ${activity.title}`
            }
          }
        }
      });
      const message = data?.sendMessage;
      if (!message) {
        throw new Error("No data returned");
      }
      close(); // Close before setting top notice
      setTopNotice(
        <React.Fragment>
          <span>{t`Your request was sent.`}</span>{" "}
          {message.thread_uid && (
            <Trans t={t}>
              <span>
                View your conversation{" "}
                <Link
                  to={Routes.websiteRoutes.adultThreadPathV2(
                    userMode,
                    message.thread_uid
                  )}
                >
                  here
                </Link>
                .
              </span>
            </Trans>
          )}
        </React.Fragment>
      );
      close();
    } catch (e) {
      setIsSendingPrivateMessage(false);
      setError(e);
    }
  };
  if (loading) {
    return null;
  }

  const noTimesMessage = t`Sorry, there are no alternate times currently \
available to transfer into. You can message the teacher with any further questions.`;
  return (
    <React.Fragment>
      <ActivitySummary
        activity={activity}
        startTime={section.start_time}
        endTime={section.end_time}
        nextOngoingMeeting={section.nextOngoingMeeting}
      />
      <Typography variant="h3">
        {t`Request transfer to another time of this class`}
      </Typography>
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
      {options.length ? (
        <form onSubmit={handleSubmit}>
          {t`Choose an available time:`}
          <Select
            name="refundReason"
            options={options}
            selectRef={sectionInputRef}
            value={requestedSection}
            placeholder={t`Choose a time...`}
            onChange={(value: TransferSection) => {
              setRequestedSection(value);
              setError(null);
            }}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              marginBottom: "0.5em"
            }}
          ></Select>
          <div>
            <Trans t={t}>
              <b>Subject:</b> Transfer request for {{ studentName }} in class:{" "}
              {{ activityTitle: activity.title }}
            </Trans>
          </div>
          <textarea
            value={transferNote}
            onChange={e => {
              setTransferNote(e.target.value);
              setError(null);
            }}
            onKeyDown={e => {
              e.stopPropagation();
            }}
            name="refundNoteInput"
            ref={transferNoteInputRef}
            rows={2}
            style={{
              width: "100%",
              boxSizing: "border-box",
              marginBottom: "0.5em",
              height: "auto"
            }}
          ></textarea>
          <div>
            {/* eslint-disable react/no-unescaped-entities */}
            <Trans t={t}>
              <p>
                Note: Transfers are at the teacher’s discretion, and your
                request may not be accepted.
              </p>
              <p>
                If you wish to withdraw after transferring, the refund policy
                will be applied against the earliest class time you were
                enrolled in.
              </p>
            </Trans>
            {/* eslint-enable react/no-unescaped-entities */}
          </div>
          <ModalActionButtons
            onCancel={close}
            primaryLabel={t`Request Transfer`}
            primaryType="submit"
            disabled={isSendingPrivateMessage}
          />
        </form>
      ) : (
        <React.Fragment>
          <Box
            sx={{
              textAlign: "left"
            }}
          >
            <Typography variant="inherit">{noTimesMessage}</Typography>
          </Box>
          <Box
            sx={{
              textAlign: "center"
            }}
          >
            <Button
              variant="link"
              onClick={close}
              data-test-id="modal-cancel-button"
            >
              {t`Cancel`}
            </Button>
          </Box>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
